import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Stack, IconButton, Popover, Box } from '@mui/material';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import values from '../../values/values';
import SettingsIcon from '@mui/icons-material/Settings';
import MapConfigPanel from '../specific/MapConfigPanel';
import MapWorkBorderLegend from '../specific/MapWorkBorderLegend';
import { extractWorkBorderLegendFromPaths } from '../../usecases/mapPathUtils';
import {
  buildCorridorPathsWithTurf,
  buildWorkBorderOutlines,
} from '../../usecases/workBorderOutlines';
import IconFinal from '../../assets/images/mk_fim.png';
import IconInicial from '../../assets/images/mk_inicio.png';
import IconTrator from '../../assets/images/mk_tractor.png';
import IconTratorRed from '../../assets/images/mk_tractor_red.png';

// Metros por pixel no Google Maps (Web Mercator), por zoom e latitude
const metersPerPixelAtZoom = (zoom, latDeg) => {
  const lat = typeof latDeg === 'number' ? latDeg : (latDeg?.lat ?? 0);
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
};

const WORK_BORDER_STROKE_WEIGHT_PX = 3;
const WORK_BORDER_OPACITY = 0.5;

const clearMapOverlays = (overlaysRef) => {
  overlaysRef.current.forEach((overlay) => {
    if (overlay && overlay.setMap) {
      overlay.setMap(null);
    }
  });
  overlaysRef.current = [];
};

const MapPanel = (props) => {
  const { implementWidthMeters, strokeWidthMeters, fixedLineColor } = props;
  const widthMetersForStroke = strokeWidthMeters ?? implementWidthMeters;
  const [lineColor, setLineColor] = useState(fixedLineColor ?? "#19c119");
  const [strokeWeight, setStrokeWeight] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapZoom, setMapZoom] = useState(props.zoomMap ?? 17);
  const [mapCenter, setMapCenter] = useState(props.centerMap ?? null);

  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]);
  const savedPointCirclesRef = useRef([]);
  const listenersRef = useRef([]);

  const handleColorSelect = (color) => {
    setLineColor(color);
    setAnchorEl(null);
  };

  const handleSliderChange = (newValue) => {
    setStrokeWeight(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openMenu = Boolean(anchorEl);

  const workBorderLegend = useMemo(
    () => extractWorkBorderLegendFromPaths(props.linhas),
    [props.linhas]
  );

  const mPerPx = mapCenter?.lat != null ? metersPerPixelAtZoom(mapZoom, mapCenter.lat) : 0.5;
  // Largura em metros do traçado ativo (implemento ou conversão do slider em px)
  const trailWidthMeters =
    widthMetersForStroke != null && widthMetersForStroke > 0
      ? widthMetersForStroke
      : (mPerPx > 0 ? strokeWeight * mPerPx : null);

  const pushPolyline = (path, strokeColor, strokeOpacity, strokeWeightPx) => {
    const polyline = new window.google.maps.Polyline({
      path,
      strokeColor,
      strokeOpacity,
      strokeWeight: strokeWeightPx,
      map: mapInstanceRef.current,
      clickable: false,
    });
    overlaysRef.current.push(polyline);
  };

  // Preenchimento = cor da métrica (sem borda por segmento)
  const pushCorridorPolygons = (corridorPaths, fillColor, fillOpacity) => {
    corridorPaths.forEach((corridorPath) => {
      const polygon = new window.google.maps.Polygon({
        paths: corridorPath,
        strokeColor: fillColor,
        strokeOpacity: 0,
        strokeWeight: 0,
        fillColor,
        fillOpacity,
        map: mapInstanceRef.current,
        geodesic: true,
        clickable: false,
      });
      overlaysRef.current.push(polygon);
    });
  };

  // Contorno externo unificado por trabalho (identificação no overlay multi-trabalho)
  const pushWorkBorderOutlines = (outlines) => {
    if (!Array.isArray(outlines) || outlines.length === 0) {
      return;
    }
    outlines.forEach((outline) => {
      (outline.rings || []).forEach((ring) => {
        if (!Array.isArray(ring) || ring.length < 3) {
          return;
        }
        const polygon = new window.google.maps.Polygon({
          paths: ring,
          strokeColor: outline.color,
          strokeOpacity: WORK_BORDER_OPACITY,
          strokeWeight: WORK_BORDER_STROKE_WEIGHT_PX,
          fillColor: outline.color,
          fillOpacity: 0,
          map: mapInstanceRef.current,
          geodesic: true,
          clickable: false,
          zIndex: 3,
        });
        overlaysRef.current.push(polygon);
      });
    });
  };

  // Quando props.linhas ou o mapa mudar, redesenha os traçados (Polygon corredor / Polyline fina)
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google) {
      return;
    }

    if (!props.linhas || props.linhas.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      clearMapOverlays(overlaysRef);

      props.linhas.forEach((linha) => {
        if (!linha.path || linha.path.length < 2) {
          return;
        }

        const opacity = linha.opacity ?? 0.5;
        if (opacity <= 0) {
          return;
        }

        const color = linha.thinLine
          ? (linha.color || '#9e9e9e')
          : (fixedLineColor || linha.color || lineColor);

        // Linha cinza fina (inativa): Polyline basta (sempre << 32px)
        if (linha.thinLine) {
          pushPolyline(linha.path, color, opacity, linha.strokeWeight ?? 2);
          return;
        }

        const widthMeters = linha.strokeWidthMeters > 0
          ? linha.strokeWidthMeters
          : trailWidthMeters;

        if (!(widthMeters > 0)) {
          const fallbackPx = Math.max(3, Math.min(32, strokeWeight));
          pushPolyline(linha.path, color, opacity, fallbackPx);
          return;
        }

        const corridorPaths = buildCorridorPathsWithTurf(linha.path, widthMeters);
        if (!corridorPaths || corridorPaths.length === 0) {
          const fallbackPx = Math.max(3, Math.min(32, widthMeters / Math.max(mPerPx, 0.01)));
          pushPolyline(linha.path, color, opacity, fallbackPx);
          return;
        }

        pushCorridorPolygons(corridorPaths, color, opacity);
      });

      // Envelope multi-trabalho a partir das linhas originais (metadados de borda)
      const outlines = buildWorkBorderOutlines(props.linhas, trailWidthMeters);
      pushWorkBorderOutlines(outlines);
    }, 100);

    return () => {
      clearTimeout(timer);
      clearMapOverlays(overlaysRef);
    };
  }, [props.linhas, mapLoaded, lineColor, strokeWeight, trailWidthMeters, fixedLineColor, mPerPx]);

  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google) {
      return;
    }

    savedPointCirclesRef.current.forEach((circle) => {
      if (circle && circle.setMap) {
        circle.setMap(null);
      }
    });
    savedPointCirclesRef.current = [];

    if (!props.savedPoints || props.savedPoints.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      props.savedPoints.forEach((point) => {
        if (point?.lat == null || point?.lng == null) {
          return;
        }

        const pointColor = fixedLineColor || point.color || '#000000';
        const circle = new window.google.maps.Circle({
          strokeColor: pointColor,
          strokeOpacity: 1,
          strokeWeight: 1,
          fillColor: pointColor,
          fillOpacity: 1,
          map: mapInstanceRef.current,
          center: { lat: point.lat, lng: point.lng },
          radius: 0.8,
          clickable: false,
          zIndex: 2,
        });

        savedPointCirclesRef.current.push(circle);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      savedPointCirclesRef.current.forEach((circle) => {
        if (circle && circle.setMap) {
          circle.setMap(null);
        }
      });
      savedPointCirclesRef.current = [];
    };
  }, [props.savedPoints, mapLoaded, fixedLineColor]);

  const renderMarkers = () => {
    if (props.markers && props.markers.length) {
      return props.markers.map((item, i) => {
        let icon = IconTrator;

        if (item.status) {
          if (item.status !== "INICIADO") {
            icon = IconTratorRed;
          } else {
            icon = IconTrator;
          }
        }

        if (item.coordinateType === 'last') {
          icon = IconFinal;
        } else if (item.coordinateType === 'first') {
          icon = IconInicial;
        }

        return (
          <Marker
            key={i}
            position={{
              lat: item.latitude,
              lng: item.longitude,
            }}
            icon={{
              url: icon,
              scaledSize: new window.google.maps.Size(35, 45)
            }}
          />
        );
      });
    } else {
      return null;
    }
  };

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['geometry'],
  });

  if (loadError) return 'Error loading maps';
  if (!isLoaded) return 'Loading maps';

  return (
    <Stack spacing={values.spacing[0]} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        {isLoaded && (
          <GoogleMap
          mapContainerStyle={{
            height: props.height ? props.height : 300,
            width: '100%',
            maxWidth: '100%',
          }}
          zoom={props.zoomMap}
          center={props.centerMap}
          mapTypeId="satellite"
          onLoad={(map) => {
            mapInstanceRef.current = map;
            setMapLoaded(true);
            setMapZoom(map.getZoom());
            const c = map.getCenter();
            if (c) setMapCenter(c.toJSON());

            const updateZoomAndCenter = () => {
              setMapZoom(map.getZoom());
              const center = map.getCenter();
              if (center) setMapCenter(center.toJSON());
            };

            const z = map.addListener('zoom_changed', updateZoomAndCenter);
            const i = map.addListener('idle', updateZoomAndCenter);
            listenersRef.current = [z, i];
          }}
          onUnmount={() => {
            listenersRef.current.forEach(l => { if (l && typeof l.remove === 'function') l.remove(); });
            listenersRef.current = [];
            clearMapOverlays(overlaysRef);
            savedPointCirclesRef.current.forEach((circle) => {
              if (circle && circle.setMap) {
                circle.setMap(null);
              }
            });
            savedPointCirclesRef.current = [];
          }}
          >
            <div style={{ position: 'absolute', top: '60px', right: '15px' }}>
              <IconButton onClick={handleMenuOpen} size="small">
                <SettingsIcon />
              </IconButton>

          </div>
            {renderMarkers()}
          </GoogleMap>
        )}
        <MapWorkBorderLegend items={workBorderLegend} />
        <Popover
          open={openMenu}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box p={2}>
            <MapConfigPanel
              lineColor={lineColor}
              onLineColorChange={handleColorSelect}
              strokeWeight={strokeWeight}
              onStrokeWeightChange={handleSliderChange}
              useImplementWidth={widthMetersForStroke != null && widthMetersForStroke > 0}
              hideLineColor={Boolean(fixedLineColor)}
            />
          </Box>
        </Popover>
    </Stack>
  );
};
export default MapPanel;
